import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Legend } from 'recharts';
import { Target, Megaphone, DollarSign, AlertCircle, Share2, Twitter, Linkedin, Mail, Link2, CheckCircle2, XCircle, AlertTriangle, Flame } from 'lucide-react';
import { MoneyAlert, BlockAnalysisCard } from '../components/results/ResultsSections';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BLOCK_ICONS = { oferta: Target, marketing: Megaphone, ventas: DollarSign };

const getScoreIcon = (score, maxScore) => {
  const pct = (score / maxScore) * 100;
  if (pct >= 90) return { icon: CheckCircle2, color: '#7df9c5' };
  if (pct >= 70) return { icon: CheckCircle2, color: '#84cc16' };
  if (pct >= 50) return { icon: AlertTriangle, color: '#eab308' };
  return { icon: XCircle, color: '#ef4444' };
};

const getScoreEmoji = (score) => {
  if (score <= 25) return '🚨';
  if (score <= 35) return '⚠️';
  if (score <= 45) return '⚡';
  if (score <= 55) return '📊';
  return '🎯';
};

const ResultsPage = () => {
  const { resultId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const response = await axios.get(`${API}/quiz/result/${resultId}`);
        setResult(response.data);
        
        if (window.fbq) {
          window.fbq('track', 'ViewContent', {
            content_name: 'Quiz Results',
            content_category: 'Results Page',
            value: response.data.total_score,
            currency: 'EUR'
          });
          window.fbq('trackCustom', 'ResultView', {
            content_name: 'Quiz Results Page',
            score: response.data.total_score,
            page: 'ResultsPage'
          });
        }
      } catch (fetchErr) {
        setError('No se pudo cargar el resultado. Verifica el enlace.');
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [resultId]);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://lnk.superleads.io/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const shareUrl = window.location.href;
  const shareText = `Acabo de hacer el diagnóstico comercial de Sales Boost. Mi puntuación: ${result?.total_score}/60. ¿Cómo está tu sistema comercial?`;

  const handleShare = useCallback((platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent('Mi diagnóstico comercial')}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`
    };

    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    
    if (window.fbq) {
      window.fbq('track', 'Share', { content_name: 'Quiz Results', method: platform });
    }
  }, [shareText, shareUrl]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [shareUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#802ef8] mx-auto mb-4"></div>
          <p className="text-white text-lg">Generando tu diagnóstico...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl text-red-500 mb-4" data-testid="error-message">{error}</p>
        </div>
      </div>
    );
  }

  if (!result) return null;

  const radarData = [
    { area: 'Oferta', score: result.block_scores.oferta, fullMark: 20 },
    { area: 'Marketing', score: result.block_scores.marketing, fullMark: 20 },
    { area: 'Ventas', score: result.block_scores.ventas, fullMark: 20 }
  ];

  const pieData = [
    { name: 'Completado', value: result.total_score, fill: '#802ef8' },
    { name: 'Potencial perdido', value: 60 - result.total_score, fill: '#262626' }
  ];

  const blockData = [
    { key: 'oferta', name: 'Oferta y Posicionamiento', score: result.block_scores.oferta },
    { key: 'marketing', name: 'Marketing y Oportunidades', score: result.block_scores.marketing },
    { key: 'ventas', name: 'Ventas y Cierre', score: result.block_scores.ventas }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pb-16 sm:pb-24">
      {/* Logo */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-50">
        <img 
          src="https://customer-assets.emergentagent.com/job_sales-audit-4/artifacts/laaotby8_v1_white.png" 
          alt="Sales Boost"
          className="h-10 md:h-12 lg:h-16 w-auto opacity-90"
        />
      </div>

      {/* Share Button */}
      <div className="fixed top-4 right-4 md:top-8 md:right-8 z-50">
        <div className="relative">
          <button
            onClick={() => setShowShareMenu(!showShareMenu)}
            className="bg-[#141414] border border-[#262626] hover:border-[#802ef8] px-4 py-2 rounded-md transition-all flex items-center gap-2"
            data-testid="share-button"
          >
            <Share2 className="w-4 h-4" />
            <span className="hidden md:inline">Compartir</span>
          </button>

          {showShareMenu && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute top-full right-0 mt-2 bg-[#141414] border border-[#262626] rounded-md p-4 min-w-[200px] shadow-xl"
            >
              <div className="space-y-2">
                <button onClick={() => handleShare('twitter')} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#262626] rounded-md transition-colors">
                  <Twitter className="w-4 h-4" /><span>Twitter</span>
                </button>
                <button onClick={() => handleShare('linkedin')} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#262626] rounded-md transition-colors">
                  <Linkedin className="w-4 h-4" /><span>LinkedIn</span>
                </button>
                <button onClick={() => handleShare('email')} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#262626] rounded-md transition-colors">
                  <Mail className="w-4 h-4" /><span>Email</span>
                </button>
                <button onClick={copyToClipboard} className="w-full flex items-center gap-3 px-4 py-2 hover:bg-[#262626] rounded-md transition-colors">
                  <Link2 className="w-4 h-4" /><span>{copied ? '¡Copiado!' : 'Copiar enlace'}</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Header con Alerta */}
      <div className="border-b-2 border-red-600 bg-gradient-to-r from-red-950/50 to-orange-950/50 mt-16 sm:mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-start gap-3 sm:gap-4">
            <Flame className="w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 text-red-500 flex-shrink-0 animate-pulse" />
            <div className="min-w-0">
              <h1 
                className="text-lg sm:text-2xl md:text-3xl font-bold tracking-tight mb-2 break-words"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Diagnóstico Comercial de {result.lead_data.company}
              </h1>
              {result.ai_analysis?.urgency && (
                <p className="text-sm sm:text-lg md:text-xl text-red-400 font-semibold">
                  {result.ai_analysis.urgency}
                </p>
              )}
              <p className="text-xs sm:text-sm text-[#666666] mt-2">
                {new Date(result.timestamp).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* ALERTA MÁXIMA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <MoneyAlert result={result} />
        </motion.div>

        {/* Tu Puntuación */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#141414] border-2 border-[#262626] rounded-md p-6 sm:p-8">
              <div className="text-center">
                <div className="text-5xl sm:text-6xl md:text-8xl font-bold mb-2" style={{ color: result.level_color }}>
                  {result.total_score}<span className="text-2xl sm:text-3xl md:text-4xl text-[#666666]">/60</span>
                </div>
                <div className="h-3 rounded-full mb-4 w-full" style={{ backgroundColor: result.level_color }} />
                <p className="text-xl md:text-2xl text-[#a3a3a3] mb-4">{result.level}</p>
                <div className="text-6xl mb-4">{getScoreEmoji(result.total_score)}</div>
              </div>
            </div>

            <div className="bg-[#141414] border-2 border-[#262626] rounded-md p-6 sm:p-8">
              <h3 className="text-base sm:text-lg font-bold mb-4 text-center">Potencial Aprovechado</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={5} dataKey="value" />
                  <Tooltip contentStyle={{ backgroundColor: '#141414', border: '1px solid #262626', borderRadius: '4px' }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center mt-4">
                <p className="text-4xl font-bold text-[#802ef8]">{Math.round((result.total_score / 60) * 100)}%</p>
                <p className="text-xs sm:text-sm text-[#666666]">aprovechado del 100%</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Análisis por Área */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-16"
        >
          <h2 
            className="text-xl sm:text-2xl md:text-4xl font-bold mb-6 sm:mb-8 flex items-center gap-2 sm:gap-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 flex-shrink-0" />
            Análisis Detallado por Área
          </h2>

          <div className="grid gap-6">
            {blockData.map((block, index) => {
              const Icon = BLOCK_ICONS[block.key] || AlertCircle;
              const scoreIconData = getScoreIcon(block.score, 20);
              return (
                <motion.div
                  key={block.key}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                >
                  <BlockAnalysisCard
                    block={block}
                    Icon={Icon}
                    ScoreIcon={scoreIconData.icon}
                    scoreIconData={scoreIconData}
                    aiAnalysis={result.ai_analysis?.[block.key]}
                    index={index}
                  />
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Radar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-16"
        >
          <div className="bg-[#141414] border border-[#262626] rounded-md p-4 sm:p-6 md:p-8">
            <h3 className="text-base sm:text-lg font-bold mb-4 text-center">Vista Global del Sistema</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#262626" />
                <PolarAngleAxis dataKey="area" tick={{ fill: '#a3a3a3', fontSize: 14 }} />
                <PolarRadiusAxis angle={90} domain={[0, 20]} tick={{ fill: '#666666' }} />
                <Radar name="Tu Puntuación" dataKey="score" stroke="#802ef8" fill="#802ef8" fillOpacity={0.3} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* CTA Final */}
        {result.total_score < 60 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-gradient-to-br from-[#802ef8]/20 to-[#7df9c5]/20 border-2 border-[#802ef8] rounded-md p-6 sm:p-8 md:p-12"
          >
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                ¿Listo para arreglar esto de una vez?
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-[#a3a3a3] mb-6 sm:mb-8">
                Mira este video de 2 minutos para entender cómo podemos ayudarte a cerrar estos gaps.
              </p>
            </div>

            <div className="mb-8">
              <div 
                className="relative border border-[#262626] rounded-md overflow-hidden bg-[#141414] mx-auto"
                style={{ maxWidth: '400px' }}
              >
                <div style={{ padding: '177.78% 0 0 0', position: 'relative' }}>
                  <iframe
                    src="https://player.vimeo.com/video/1180946717?badge=0&autopause=0&player_id=0&app_id=58479"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title="VSL Principal"
                  />
                </div>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => setShowCalendarModal(true)}
                className="inline-block px-8 py-4 bg-[#802ef8] hover:bg-[#9b5aff] rounded-md text-lg font-semibold transition-all hover:scale-105 shadow-lg shadow-[#802ef8]/20 mb-4"
                data-testid="book-session-button"
              >
                Agendar sesión gratuita ahora →
              </button>
              <p className="text-sm text-[#666666]">30 minutos. Sin compromiso.</p>
            </div>
          </motion.div>
        )}

        {/* Calendar Modal */}
        <AnimatePresence>
          {showCalendarModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-2 sm:p-4"
              onClick={() => setShowCalendarModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-[#0a0a0a] border-2 border-[#802ef8] rounded-lg w-full max-w-5xl h-[98vh] sm:h-[95vh] flex flex-col overflow-hidden shadow-2xl shadow-[#802ef8]/20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 sm:p-6 border-b-2 border-[#802ef8] flex-shrink-0 bg-[#141414]">
                  <div>
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Agenda tu sesión de diagnóstico
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-[#a3a3a3] mt-1 sm:mt-2">
                      30 minutos · Completamente gratis · Sin compromiso
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCalendarModal(false)}
                    className="text-[#666666] hover:text-white transition-colors p-1 sm:p-2 hover:bg-[#262626] rounded-md flex-shrink-0"
                    data-testid="close-calendar-modal"
                  >
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto bg-white">
                  <iframe 
                    src="https://lnk.superleads.io/widget/booking/D5N1KHKzc1uaTyniwbdE" 
                    style={{ width: '100%', height: '100%', minHeight: '600px', border: 'none' }}
                    title="Sales Boost Calendar"
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12 text-center text-sm text-[#666666]">
          <p>Diagnóstico generado por Sales Boost HTC · salesboost.com</p>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
